
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { useRef } from "react";
import { GetServerSidePropsContext } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import { getApolloClient, addApolloState } from "@faustwp/core";
import { Flex, Box, Text } from "rebass/styled-components";
import ReactHtmlParser from "react-html-parser";
import MobileApp from "../components/promotion/MobileApp";
import CategoriesBlock from "../components/sections/CategoriesBlock";
import SubscribeBlock from "../components/promotion/SubscribeBlock";
import Author from "../components/sections/Author";
import Post from "../components/Post";
import SecondaryPosts from "../components/sections/SecondaryPosts";
import { getPostWithImg } from ".";
import FindSitter from "../components/promotion/FindSitter";
import InfoBlock from "../components/promotion/InfoBlock";
import useIsMobile from "../hooks/useIsMobile";
import Layout from "../components/Layout";
import Loader from "../components/Loader";
import NewComment from "../components/sections/NewComment";
import Comments from "../components/sections/Comments";
import FeaturedImage from "../components/common/FeaturedImage";
import { useGetMediaDataQuery, useGetNextPostQuery, useGetPostQuery, useGetPreviousPostQuery, useGetRelatedCategoryPostsQuery, GetPostDocument, GetMenusDocument, } from "../generated/graphql";
import { CategoryType, InferredAuthor, InferredComment, InferredCursor, InferredMedia, InferredPost, InferredSinglePost, } from "../types";
import theme from "../styles/theme";
import styles from "../styles/base.module.scss";
export default function Article() {
    const isMobile = useIsMobile();
    const router = useRouter();
    const { t } = useTranslation("common");
    // Queries
    const postName = router.query.article as string;
    const { data: postData, loading } = useGetPostQuery({
        variables: { postName: postName },
    });
    const getRelatedPosts = (categoryId: string, postId: string) => {
        const { data: postsData } = useGetRelatedCategoryPostsQuery({
            variables: { categoryId: categoryId, postId: postId },
        });
        const posts: InferredPost[] = postsData?.category?.posts?.nodes || [];
        return posts;
    };
    const getImages = (postIds: string[]) => {
        const { data: mediaData } = useGetMediaDataQuery({
            variables: { postIds: postIds || [""] },
        });
        const newMedia: InferredMedia[] = mediaData?.mediaItems?.nodes || [];
        return newMedia;
    };
    const getNextpost = (cursor: any) => {
        const { data } = useGetNextPostQuery({ variables: { cursor: cursor } });
        return data?.posts?.nodes[0]?.uri;
    };
    const getPreviousPost = (cursor: any) => {
        const { data } = useGetPreviousPostQuery({ variables: { cursor: cursor } });
        return data?.posts?.nodes[0]?.uri;
    };
    // Post
    const post: InferredSinglePost = postData?.posts?.nodes[0] || { id: "" };
    const categories: CategoryType[] = post?.categories?.nodes || [];
    const author: InferredAuthor = post.author?.node || {};
    const comments: InferredComment[] = post.comments?.nodes || [];
    const postEdge: InferredCursor = postData?.posts?.edges[0] || {};
    const postCursor = postEdge.cursor;
    const relatedPosts = getRelatedPosts(categories[0]?.id, post.id) || [];
    const media = getImages(relatedPosts.map((post) => post.id));
    const postWithImg = getPostWithImg(relatedPosts, media) || [];
    const featImgUrl = post?.featuredImage?.node?.sourceUrl;
    // Mobile swiping
    const nextPostUri = getNextpost(postCursor);
    const prevPostUri = getPreviousPost(postCursor);
    const touchStartX = useRef(0);
    // const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    //   event.preventDefault();
    //   touchStartX.current = event.touches[0].clientX;
    // };
    // const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    //   // Prevent scrolling while detecting the swipe
    //   event.preventDefault();
    // };
    // const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    //   event.preventDefault();
    //   const touchEndX = event.changedTouches[0].clientX;
    //   const touchDistanceX = touchEndX - touchStartX.current;
    //   // Check if the swipe distance is greater than a threshold (e.g., 50px)
    //   if (Math.abs(touchDistanceX) > 50) {
    //     if (touchDistanceX > 0) {
    //       prevPostUri && router.push(prevPostUri);
    //     } else {
    //       nextPostUri && router.push(nextPostUri);
    //     }
    //   }
    // };
    // Rendering
    const renderPosts = () => {
        return postWithImg.map((post, index) => (<Post key={index} postData={post} postType="scndPost" numCategories={1}/>));
    };
    const renderContent = () => {
        let numBlocks = 0;
        const blocks: Record<number, JSX.Element> = {
            0: <FindSitter origin="article"/>,
            1: <InfoBlock type={"column"} showMedia={false}/>,
            // 1: <SubscribeBlock type={isMobile ? "column" : "row"}  />,
        };
        const numKeys = Object.keys(blocks).length;
        const modifiedParsed = ReactHtmlParser(post?.content || "").map((node, index) => {
            if (node.type === "h3" || node.type === "h4") {
                if (numBlocks <= numKeys - 2) {
                    // we want the last block at the end
                    const element = (<Box key={index}>
                <Box sx={{ my: "32px" }}>{blocks[numBlocks]}</Box>
                {node}
              </Box>);
                    numBlocks += 1;
                    return element;
                }
                else
                    return node;
            }
            else
                return node;
        });
        const lastBlock = (<Box>
        <Box sx={{ my: "32px" }}>{blocks[numKeys - 1]}</Box>
      </Box>);
        modifiedParsed.push(lastBlock);
        return modifiedParsed;
    };
    return (<Layout seoTitle={post.seo?.title ?? undefined} seoDescription={post.seo?.metaDesc ?? undefined}>
      <Flex sx={{ flexDirection: ["column", "column", "row"], gap: "32px" }}>
        <Flex sx={{
            flexDirection: "column",
            width: ["100%", "100%", "70%"],
            gap: "24px",
        }}>
          <Text as="h1" sx={{ ...theme.mainTitle }}>
            {post.title}
          </Text>

          <CategoriesBlock categories={categories} variant="article"/>

          {!!featImgUrl && <FeaturedImage url={featImgUrl} width="100%"/>}

          <div className="postContent">
            <div className={styles.container}>{renderContent()}</div>
          </div>

          <Author authorInfo={author}/>

          <Comments comments={comments} isMobile={isMobile}/>

          {/* <NewComment /> */}

          <Box sx={{ my: "48px" }}>
            <Text as="h2" sx={{ ...theme.mainTitle, mb: "20px" }}>
              {t("alsoRecommended")}
            </Text>
            <SecondaryPosts>{renderPosts()}</SecondaryPosts>
          </Box>

          <SubscribeBlock type={isMobile ? "column" : "row"}/>
        </Flex>

        <Box sx={{ width: ["100%", "100%", "30%"] }}>
          <Box mb={3}>
            <SubscribeBlock type="column"/>
          </Box>
          {!isMobile && <MobileApp type="column" isMobile={isMobile}/>}
        </Box>
      </Flex>
    </Layout>);
}
const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const { query } = context;
    const postName = query.article as string;
    const variables = { postName: postName };
    const apolloClient = getApolloClient();
    const result = await Promise.all([
        apolloClient.query({
            query: GetPostDocument,
            variables,
        }),
        apolloClient.query({ query: GetMenusDocument }),
    ]);
    if (!result[0].data.posts.nodes[0]) {
        return {
            notFound: true,
        };
    }
    return addApolloState(apolloClient, {
        props: {},
    });
};

    async function __Next_Translate__getServerSideProps__1986b5c16c6__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/[article]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1986b5c16c6__ as getServerSideProps }
  